<template>
  <vue-final-modal
    classes="flex justify-center items-center"
    v-model="modalVisible"
  >
    <div class="bg-white rounded-lg shadow-2xl mx-4 flex flex-col overflow-hidden" style="width: 36rem; max-width: calc(100vw - 2rem)">
      <!-- Header -->
      <div class="px-6 pt-5 pb-4 border-b border-gray-100 bg-white flex-shrink-0">
        <div class="flex items-start justify-between">
          <div class="flex items-center">
            <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mr-3">
              <i class="fa-solid fa-clock-rotate-left text-blue-600"></i>
            </div>
            <div>
              <h3 class="text-base font-semibold text-gray-900 leading-tight m-0">Version history</h3>
              <p class="text-xs text-gray-500 mt-1 m-0">{{ summaryText }}</p>
            </div>
          </div>
          <button
            @click="handleClose"
            class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 bg-transparent border-0 cursor-pointer"
            aria-label="Close"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Search + filter -->
        <div class="mt-4 flex items-center">
          <div class="flex-1 flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 mr-2 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200">
            <i class="fa-solid fa-magnifying-glass text-gray-400 text-xs mr-2"></i>
            <input
              v-model="search"
              type="text"
              placeholder="Search by version, note, or author..."
              class="flex-1 min-w-0 bg-transparent border-0 outline-none py-2 text-sm text-gray-900 placeholder-gray-400"
            />
            <button
              v-if="search"
              @click="search = ''"
              class="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer ml-1"
              aria-label="Clear search"
            >
              <i class="fa-solid fa-xmark text-xs"></i>
            </button>
          </div>
          <div class="flex items-center bg-gray-100 rounded-lg p-1 text-xs">
            <button
              v-for="opt in filterOptions"
              :key="opt.key"
              @click="filter = opt.key"
              class="px-3 py-1 rounded-md font-medium border-0 cursor-pointer"
              :class="filter === opt.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-700'"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="px-6 py-3 overflow-y-auto" style="height: 24rem">
        <div v-if="loading" class="py-12 flex flex-col items-center text-gray-400">
          <i class="fa-solid fa-circle-notch fa-spin text-2xl mb-2"></i>
          <p class="text-sm m-0">Loading history...</p>
        </div>
        <div v-else-if="!history.length" class="py-12 flex flex-col items-center text-gray-400">
          <i class="fa-solid fa-inbox text-3xl mb-2"></i>
          <p class="text-sm m-0">No versions yet</p>
        </div>
        <div v-else-if="!filteredHistory.length" class="py-12 flex flex-col items-center text-gray-400">
          <i class="fa-solid fa-magnifying-glass text-2xl mb-2"></i>
          <p class="text-sm m-0">No versions match "{{ search }}"</p>
        </div>
        <ul v-else class="m-0 p-0 list-none">
          <li
            v-for="entry in filteredHistory"
            :key="entry.version || entry.created_at"
            class="rounded-md border mb-1 last:mb-0"
            :class="entry.version_deleted
              ? 'bg-red-50 border-red-100'
              : canSelect(entry)
                ? 'bg-white border-gray-100 hover:border-blue-300 hover:shadow-sm cursor-pointer'
                : 'bg-white border-gray-100'"
            @click="canSelect(entry) && handleSelect(entry)"
          >
            <div class="px-2 py-2">
              <!-- Top row: version chip + badges + delete button -->
              <div class="flex items-center flex-wrap">
                <span
                  class="inline-flex items-center font-mono font-semibold text-xs px-2 rounded"
                  :class="entry.version_deleted
                    ? 'bg-red-100 text-red-700 line-through'
                    : 'bg-gray-100 text-gray-800'"
                >
                  {{ entry.version || '—' }}
                </span>
                <span
                  v-if="entry.is_current"
                  class="inline-flex items-center text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2 ml-2 leading-tight"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1"></span>
                  Current
                </span>
                <span
                  v-if="entry.is_published"
                  class="inline-flex items-center text-xs font-medium bg-green-50 text-green-700 border border-green-100 rounded-full px-2 ml-2 leading-tight"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-green-500 mr-1"></span>
                  Published
                </span>
                <span
                  v-if="entry.version_deleted"
                  class="inline-flex items-center text-xs font-medium bg-red-50 text-red-700 border border-red-100 rounded-full px-2 ml-2 leading-tight"
                >
                  <i class="fa-solid fa-trash-can fa-xs mr-1"></i>
                  Deleted
                </span>
                <span
                  v-if="entry.source_version"
                  class="inline-flex items-center text-xs text-gray-500 ml-2 leading-tight"
                  :title="`Created from version ${entry.source_version}`"
                >
                  <i class="fa-solid fa-code-branch fa-xs mr-1"></i>
                  from
                  <span class="font-mono font-semibold ml-1">{{ entry.source_version }}</span>
                </span>
                <button
                  v-if="canDelete(entry)"
                  @click.stop="$emit('requestDelete', entry.version)"
                  class="ml-auto w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded bg-transparent border-0 cursor-pointer"
                  :title="`Delete version ${entry.version}`"
                  aria-label="Delete version"
                >
                  <i class="fa-solid fa-trash-can text-xs"></i>
                </button>
              </div>

              <!-- Note -->
              <p
                v-if="entry.note"
                :title="entry.note"
                class="text-xs text-gray-700 mt-1 mb-0 truncate cursor-help"
              >
                {{ entry.note }}
              </p>
              <p v-else class="text-xs text-gray-400 italic mt-1 mb-0">
                No note recorded
              </p>

              <!-- Created meta -->
              <div class="flex items-center flex-wrap text-xs text-gray-500 mt-1">
                <span class="flex items-center">
                  <i class="fa-regular fa-calendar mr-1"></i>
                  {{ formatDate(entry.created_at) }}
                </span>
                <span v-if="entry.created_by" class="flex items-center ml-3">
                  <i class="fa-regular fa-user mr-1"></i>
                  {{ entry.created_by.name || entry.created_by.email }}
                </span>
              </div>

              <!-- Deletion block -->
              <div v-if="entry.version_deleted" class="mt-2 pt-2 border-t border-red-100 text-xs">
                <div class="flex items-center flex-wrap text-red-700">
                  <span class="flex items-center">
                    <i class="fa-solid fa-trash-can mr-1"></i>
                    Deleted {{ formatDate(entry.version_deleted_at) }}
                  </span>
                  <span v-if="entry.version_deleted_by" class="flex items-center ml-3">
                    <i class="fa-regular fa-user mr-1"></i>
                    {{ entry.version_deleted_by.name || entry.version_deleted_by.email }}
                  </span>
                </div>
                <p
                  v-if="entry.version_delete_note"
                  :title="entry.version_delete_note"
                  class="text-red-700 mt-1 mb-0 truncate cursor-help"
                >
                  <span class="font-medium">Reason:</span>
                  {{ entry.version_delete_note }}
                </p>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50 flex-shrink-0">
        <span class="text-xs text-gray-400">
          {{ filteredHistory.length }} of {{ history.length }}
        </span>
        <button
          @click="handleClose"
          class="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-800 cursor-pointer shadow-sm"
        >
          Close
        </button>
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
import { compareVersions, parseVersion } from '../utilities/versionUtils'

export default {
  name: 'VersionHistoryModal',
  props: {
    isVisible: { type: Boolean, default: false },
    history: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      filter: 'all',
      filterOptions: [
        { key: 'all', label: 'All' },
        { key: 'live', label: 'Live' },
        { key: 'deleted', label: 'Deleted' },
      ],
    }
  },
  computed: {
    modalVisible: {
      get() { return this.isVisible },
      set(v) { this.$emit('update:isVisible', v) },
    },
    sortedHistory() {
      const list = [...this.history]
      list.sort((a, b) => {
        const ta = parseVersion(a.version)
        const tb = parseVersion(b.version)
        if (ta && tb) return compareVersions(b.version, a.version)
        if (ta && !tb) return -1
        if (!ta && tb) return 1
        return 0
      })
      return list
    },
    filteredHistory() {
      const q = this.search.trim().toLowerCase()
      return this.sortedHistory.filter(entry => {
        if (this.filter === 'live' && entry.version_deleted) return false
        if (this.filter === 'deleted' && !entry.version_deleted) return false
        if (!q) return true
        const haystack = [
          entry.version,
          entry.note,
          entry.version_delete_note,
          entry.created_by?.name,
          entry.created_by?.email,
          entry.version_deleted_by?.name,
          entry.version_deleted_by?.email,
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(q)
      })
    },
    summaryText() {
      const total = this.history.length
      const deleted = this.history.filter(h => h.version_deleted).length
      const live = total - deleted
      if (total === 0) return 'No versions'
      if (deleted === 0) return `${live} version${live === 1 ? '' : 's'}`
      return `${live} live · ${deleted} deleted`
    },
  },
  watch: {
    isVisible(v) {
      if (v) {
        this.search = ''
        this.filter = 'all'
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit('update:isVisible', false)
    },
    canDelete(entry) {
      return !entry.version_deleted && !entry.is_current && !entry.is_published
    },
    canSelect(entry) {
      return !entry.version_deleted && !!entry.version
    },
    handleSelect(entry) {
      this.$emit('selectVersion', entry.version)
      this.$emit('update:isVisible', false)
    },
    formatDate(d) {
      if (!d) return 'unknown date'
      try {
        const date = new Date(d)
        if (isNaN(date.getTime())) return 'unknown date'
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) {
        return 'unknown date'
      }
    },
  },
}
</script>
